import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Play, RefreshCw, CheckCircle2, XCircle, Clock, Loader2, Timer,
  History, ChevronRight, AlertCircle, X, Repeat, ArrowDownUp,
  ToggleLeft, ToggleRight, Unlock, Info, Pencil, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import API from '../../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cronToLabel(expr, nextRunIso) {
  if (!expr) return '—';
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [min, hour, dom, , dow] = parts;

  if (min.startsWith('*/')) return `Toutes les ${min.slice(2)} min`;
  if (hour.startsWith('*/')) return `Toutes les ${hour.slice(2)}h`;

  // Le cron est défini en UTC — on affiche l'heure dans le fuseau local de
  // l'utilisateur (déduite du prochain run calculé par le backend) pour rester
  // cohérent avec les colonnes « Dernier / Prochain » affichées en heure locale.
  const ref = nextRunIso ? new Date(nextRunIso) : null;
  const hasRef = ref && !isNaN(ref.getTime());
  const localHhmm = hasRef
    ? `${String(ref.getHours()).padStart(2, '0')}:${String(ref.getMinutes()).padStart(2, '0')}`
    : null;
  const utcHhmm = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;

  if (dom !== '*' && dow === '*') {
    if (hasRef && /^\d{1,2}$/.test(dom))
      return `Le ${ref.getDate()} du mois à ${localHhmm} (heure locale)`;
    return `Le ${dom} du mois à ${utcHhmm} UTC`;
  }
  if (dow !== '*' && dom === '*') {
    if (hasRef && /^\d$/.test(dow)) {
      const jours = ['dim','lun','mar','mer','jeu','ven','sam'];
      return `Chaque ${jours[ref.getDay()]} à ${localHhmm} (heure locale)`;
    }
    const jours = ['dim','lun','mar','mer','jeu','ven','sam'];
    return `Chaque ${jours[+dow] || dow} à ${utcHhmm} UTC`;
  }
  if (dom === '*' && dow === '*') {
    if (hasRef) return `Quotidien à ${localHhmm} (heure locale)`;
    return `Quotidien à ${utcHhmm} UTC`;
  }
  return expr;
}

function fmtDatetime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60000)} min ${Math.round((ms % 60000) / 1000)} s`;
}

// ─── Composants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  success: { label: 'Succès',         color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle2 },
  error:   { label: 'Erreur',         color: '#dc2626', bg: '#fef2f2', icon: XCircle },
  running: { label: 'En cours',       color: '#d97706', bg: '#fffbeb', icon: Loader2 },
  null:    { label: 'Jamais exécuté', color: 'var(--text-muted)', bg: 'var(--bg-subtle)', icon: Clock },
};

const JOB_TYPE_CONFIG = {
  cron: { label: 'Cron', color: '#4f46e5', bg: '#eef2ff', Icon: Repeat },
  sync: { label: 'Sync', color: '#0369a1', bg: '#e0f2fe', Icon: ArrowDownUp },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.null;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg }}>
      <Icon size={11} className={status === 'running' ? 'animate-spin' : ''} />
      {cfg.label}
    </span>
  );
}

function JobTypeBadge({ type }) {
  const cfg = JOB_TYPE_CONFIG[type] || JOB_TYPE_CONFIG.cron;
  const { Icon } = cfg;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
      style={{ color: cfg.color, background: cfg.bg }}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

/** Affiche les champs clés de last_context pour les jobs sync */
function LastContextSummary({ ctx }) {
  if (!ctx || !Object.keys(ctx).length) return null;
  const items = [
    ctx.last_sync_at  && `Dernière sync : ${fmtDatetime(ctx.last_sync_at)}`,
    ctx.nb_updated != null && ctx.nb_updated > 0 && `${ctx.nb_updated} mis à jour`,
    ctx.nb_errors  != null && ctx.nb_errors  > 0 && `${ctx.nb_errors} erreurs`,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--text-muted)' }}>
      {items.map((t, i) => <span key={i}>{t}</span>)}
    </div>
  );
}

// ─── Panneau historique ────────────────────────────────────────────────────────

function HistoryPanel({ job, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/api/admin/cron-jobs/${job.id}/logs?limit=20`)
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Impossible de charger les logs'))
      .finally(() => setLoading(false));
  }, [job.id]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.3)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="h-full w-full max-w-2xl flex flex-col bg-white shadow-2xl">

        {/* En-tête */}
        <div className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <History size={18} style={{ color: 'var(--primary-solid)' }} />
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{job.name}</p>
                <JobTypeBadge type={job.job_type} />
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>20 dernières exécutions</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
              Aucune exécution enregistrée
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {logs.map(log => {
                const cfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.null;
                const Icon = cfg.icon;
                return (
                  <div key={log.log_id} data-testid={`log-row-${log.log_id}`}
                    className="rounded-xl border p-4 space-y-2"
                    style={{ borderColor: log.status === 'error' ? '#fecaca' : 'var(--border)', background: log.status === 'error' ? '#fef2f2' : 'white' }}>
                    {/* Ligne 1 : statut + date + durée + déclencheur */}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Icon size={13} style={{ color: cfg.color, flexShrink: 0 }}
                          className={log.status === 'running' ? 'animate-spin' : ''} />
                        <span className="text-xs font-semibold" style={{ color: cfg.color }}>
                          {cfg.label}
                        </span>
                        <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {fmtDatetime(log.started_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {fmtDuration(log.duration_ms)}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                          {log.triggered_by}
                        </span>
                      </div>
                    </div>

                    {/* Ligne 2 : métriques nb_* */}
                    {(log.nb_processed > 0 || log.nb_updated > 0 || log.nb_errors > 0) && (
                      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {log.nb_processed > 0 && <span>{log.nb_processed} traités</span>}
                        {log.nb_updated > 0   && <span style={{ color: '#16a34a' }}>{log.nb_updated} mis à jour</span>}
                        {log.nb_errors > 0    && <span style={{ color: '#dc2626' }}>{log.nb_errors} erreurs</span>}
                      </div>
                    )}

                    {/* Détail */}
                    {log.detail && (
                      <p className="text-xs" style={{ color: log.status === 'error' ? '#dc2626' : 'var(--text-secondary)' }}>
                        {log.detail}
                      </p>
                    )}

                    {/* Stack trace */}
                    {log.error_trace && (
                      <details className="mt-1">
                        <summary className="text-xs cursor-pointer" style={{ color: '#dc2626' }}>
                          Voir la trace
                        </summary>
                        <pre className="mt-2 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre-wrap"
                          style={{ background: '#fee2e2', color: '#991b1b', fontSize: '10px' }}>
                          {log.error_trace}
                        </pre>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Popover documentation ────────────────────────────────────────────────────

function DocPopover({ job, onDescriptionSaved }) {
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const ref = useRef(null);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.patch(`/api/admin/cron-jobs/${job.id}/description`, { description: draft });
      onDescriptionSaved(job.id, draft);
      setEditing(false);
      toast.success('Documentation mise à jour');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!job.description && !open) return null;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={() => { setOpen(o => !o); setEditing(false); setDraft(job.description || ''); }}
        title="Voir la documentation"
        className="p-1 rounded transition-colors hover:opacity-80"
        style={{ color: 'var(--text-muted)' }}>
        <Info size={13} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 rounded-xl border shadow-xl"
             style={{ background: 'var(--bg)', borderColor: 'var(--border)', width: 480 }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b"
               style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>
              Documentation — {job.name}
            </span>
            <div className="flex items-center gap-1.5">
              {!editing && (
                <button onClick={() => { setEditing(true); setDraft(job.description || ''); }}
                        title="Modifier"
                        className="p-1 rounded hover:opacity-70"
                        style={{ color: 'var(--text-muted)' }}>
                  <Pencil size={12} />
                </button>
              )}
              <button onClick={() => { setOpen(false); setEditing(false); }}
                      className="p-1 rounded hover:opacity-70"
                      style={{ color: 'var(--text-muted)' }}>
                <X size={12} />
              </button>
            </div>
          </div>

          <div className="p-4">
            {editing ? (
              <>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  rows={12}
                  className="w-full text-xs rounded-lg border px-3 py-2 resize-y outline-none font-mono"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border)',
                    color: 'var(--text)',
                    lineHeight: 1.6,
                  }}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button onClick={() => setEditing(false)}
                          className="px-3 py-1.5 rounded-lg text-xs border"
                          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                    Annuler
                  </button>
                  <button onClick={handleSave} disabled={saving}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                          style={{ background: 'var(--primary-solid)' }}>
                    {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    Sauvegarder
                  </button>
                </div>
              </>
            ) : (
              <pre className="text-xs whitespace-pre-wrap leading-relaxed"
                   style={{ color: 'var(--text)', fontFamily: 'inherit', margin: 0 }}>
                {job.description || <span style={{ color: 'var(--text-muted)' }}>Aucune documentation. Cliquez sur ✏ pour en ajouter.</span>}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Carte d'un job ────────────────────────────────────────────────────────────

function JobCard({ job, onRun, isRunning, onHistory, onToggle, onDescriptionSaved }) {
  const inactive = job.is_active === false;
  return (
    <div data-testid={`cron-job-${job.id}`}
      className="rounded-2xl border p-5 flex flex-col gap-3"
      style={{
        background: inactive ? '#F9FAFB' : '#ffffff',
        borderColor: isRunning ? '#f59e0b' : inactive ? '#E5E7EB' : 'var(--border)',
        boxShadow:   isRunning ? '0 0 0 2px #fef3c7' : undefined,
        opacity:     inactive ? 0.75 : 1,
      }}>

      {/* Titre + badges + actions */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
              {job.name}
            </span>
            <JobTypeBadge type={job.job_type} />
            {inactive
              ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
                  background: '#F3F4F6', color: '#6B7280', fontWeight: 500 }}>Désactivé</span>
              : <StatusBadge status={isRunning ? 'running' : job.last_run_status} />
            }
            <DocPopover job={job} onDescriptionSaved={onDescriptionSaved} />
          </div>
          {job.description && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {job.description.split('\n')[0]}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Toggle activer / désactiver */}
          <button onClick={() => onToggle(job)}
            data-testid={`cron-toggle-${job.id}`}
            title={inactive ? 'Activer ce job' : 'Désactiver ce job'}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-colors"
            style={{
              borderColor: inactive ? '#D1FAE5' : '#FECACA',
              color:       inactive ? '#16A34A' : '#EF4444',
              background:  inactive ? '#F0FDF4' : '#FEF2F2',
            }}>
            {inactive
              ? <><ToggleLeft size={14} /> Activer</>
              : <><ToggleRight size={14} /> Désactiver</>
            }
          </button>

          <button onClick={() => onHistory(job)}
            data-testid={`cron-history-${job.id}`}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--bg-subtle)' }}>
            <History size={13} />
            <span className="hidden sm:inline">Historique</span>
            <ChevronRight size={12} />
          </button>

          <button onClick={() => onRun(job)} disabled={isRunning || inactive}
            data-testid={`cron-run-${job.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
            style={{ background: 'var(--primary-gradient)' }}>
            {isRunning ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {isRunning ? 'En cours…' : 'Lancer'}
          </button>
        </div>
      </div>

      {/* Méta : schedule + dernier run + prochain run */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 pt-2 border-t"
        style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
          <Clock size={11} />
          <span>{cronToLabel(job.schedule_cron, job.next_run_at)}</span>
          <span className="font-mono opacity-60 ml-1">({job.schedule_cron} UTC)</span>
        </div>

        {job.last_run_at && (
          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
            <CheckCircle2 size={11} />
            <span>Dernier : {fmtDatetime(job.last_run_at)}</span>
          </div>
        )}

        {job.next_run_at && !isRunning && (
          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
            <ChevronRight size={11} />
            <span>Prochain : {fmtDatetime(job.next_run_at)}</span>
          </div>
        )}
      </div>

      {/* last_context (sync jobs uniquement) */}
      {job.job_type === 'sync' && (
        <LastContextSummary ctx={job.last_context} />
      )}

      {/* Détail dernier run */}
      {job.last_run_detail && (
        <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-lg"
          style={{
            background: job.last_run_status === 'error' ? '#fef2f2' : 'var(--bg-subtle)',
            color:      job.last_run_status === 'error' ? '#dc2626' : 'var(--text-secondary)',
          }}>
          {job.last_run_status === 'error' && <AlertCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} />}
          <span>{job.last_run_detail}</span>
        </div>
      )}
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function CronJobs() {
  const [jobs, setJobs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [running, setRunning]       = useState({});
  const [releasing, setReleasing]   = useState(false);
  const [repairing, setRepairing]   = useState(false);
  const [historyJob, setHistoryJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/api/admin/cron-jobs');
      setJobs(r.data);
    } catch {
      toast.error('Impossible de charger les jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!jobs.some(j => j.is_running)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [jobs, load]);

  // Libère immédiatement tous les verrous fantômes (badge running sans lock actif)
  const handleReleaseAll = async () => {
    setReleasing(true);
    try {
      await API.post('/api/admin/cron-jobs/release-stale');
      toast.success('Verrous fantômes libérés', { duration: 4000 });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la libération');
    } finally {
      setReleasing(false);
    }
  };

  // Recalcule le lettrage depuis le cache local (100% SQL, aucun appel Pennylane)
  const handleRepairLettering = async () => {
    setRepairing(true);
    try {
      const res = await API.post('/api/admin/repair-lettering');
      toast.success(
        `Lettrage recalculé — ${res.data?.lines_updated ?? 0} ligne(s) sur ${res.data?.societes ?? 0} société(s)`,
        { duration: 8000 },
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du recalcul du lettrage');
    } finally {
      setRepairing(false);
    }
  };

  const handleRun = async (job) => {
    setRunning(r => ({ ...r, [job.id]: true }));
    try {
      const res = await API.post(`/api/admin/cron-jobs/${job.id}/run`);
      const isAsync = res.data?.status === 'started';
      if (isAsync) {
        toast.info(`${job.name} : lancé en arrière-plan — le statut se met à jour automatiquement`, { duration: 6000 });
      } else {
        toast.success(`${job.name} : ${res.data?.detail || 'Exécution réussie'}`, { duration: 8000 });
      }
      // Recharger immédiatement pour afficher l'état "running"
      setTimeout(load, 400);
    } catch (e) {
      toast.error(`${job.name} : ${e.response?.data?.detail || 'Erreur lors du lancement'}`, { duration: 10000 });
    } finally {
      setRunning(r => ({ ...r, [job.id]: false }));
    }
  };

  const handleToggle = async (job) => {
    try {
      const res = await API.patch(`/api/admin/cron-jobs/${job.id}/toggle`);
      const state = res.data?.is_active;
      toast.success(`${job.name} ${state ? 'activé' : 'désactivé'}`, { duration: 4000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={24} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
    </div>
  );

  const cronJobs = jobs.filter(j => j.job_type === 'cron');
  const syncJobs = jobs.filter(j => j.job_type === 'sync');

  const SectionTitle = ({ icon: Icon, label, count }) => (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={14} style={{ color: 'var(--text-muted)' }} />
      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
        {count}
      </span>
    </div>
  );

  return (
    <div className="max-w-3xl">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--bg-subtle)' }}>
            <Timer size={20} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Tâches planifiées</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {cronJobs.length} cron · {syncJobs.length} sync
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Réparation lettrage — recalcul 100% SQL depuis le cache local, sans appel Pennylane */}
          <button onClick={handleRepairLettering} disabled={repairing}
            data-testid="repair-lettering-btn"
            title="Recalcule lettered/lettering_status depuis le cache local — répare les lignes bloquées après une sync interrompue"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{ color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd' }}>
            {repairing
              ? <Loader2 size={13} className="animate-spin" />
              : <CheckCircle2 size={13} />}
            Réparer le lettrage
          </button>
          {/* Bouton libérer verrous fantômes — visible si au moins un job est bloqué */}
          {jobs.some(j => !j.is_running && j.last_run_status === 'running') && (
            <button onClick={handleReleaseAll} disabled={releasing}
              data-testid="cron-release-stale-btn"
              title="Libérer les jobs bloqués en 'En cours' dont le verrou a expiré"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{ color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a' }}>
              {releasing
                ? <Loader2 size={13} className="animate-spin" />
                : <Unlock size={13} />}
              Libérer les verrous
            </button>
          )}
          <button onClick={load} data-testid="cron-refresh-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)' }}>
            <RefreshCw size={13} />Actualiser
          </button>
        </div>
      </div>

      {/* Section Sync */}
      {syncJobs.length > 0 && (
        <div className="mb-6">
          <SectionTitle icon={ArrowDownUp} label="Synchronisations" count={syncJobs.length} />
          <div className="flex flex-col gap-3">
            {syncJobs.map(job => (
              <JobCard key={job.id} job={job}
                onRun={handleRun}
                isRunning={job.is_running || !!running[job.id]}
                onHistory={setHistoryJob}
                onToggle={handleToggle}
                onDescriptionSaved={(id, desc) => setJobs(prev => prev.map(j => j.id === id ? { ...j, description: desc } : j))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Section Cron */}
      {cronJobs.length > 0 && (
        <div>
          <SectionTitle icon={Repeat} label="Cron récurrents" count={cronJobs.length} />
          <div className="flex flex-col gap-3">
            {cronJobs.map(job => (
              <JobCard key={job.id} job={job}
                onRun={handleRun}
                isRunning={job.is_running || !!running[job.id]}
                onHistory={setHistoryJob}
                onToggle={handleToggle}
                onDescriptionSaved={(id, desc) => setJobs(prev => prev.map(j => j.id === id ? { ...j, description: desc } : j))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Panneau historique */}
      {historyJob && <HistoryPanel job={historyJob} onClose={() => setHistoryJob(null)} />}
    </div>
  );
}

